import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, Upload, X, FileText } from "lucide-react";
import { ImageViewer } from "@/components/common/ImageViewer";

interface FileUploadSectionProps {
  files: File[];
  onCameraCapture: () => void;
  onFileUpload: () => void;
  onRemoveFile: (index: number) => void;
  onImageClick?: (index: number) => void;
  imageViewerState?: {
    isOpen: boolean;
    images: string[];
    currentIndex: number;
    setCurrentIndex: (index: number) => void;
    onClose: () => void;
  };
  label?: string;
  helpText?: string;
  showLabel?: boolean;
  disabled?: boolean;
}

export function FileUploadSection({
  files,
  onCameraCapture,
  onFileUpload,
  onRemoveFile,
  onImageClick,
  imageViewerState,
  label = "Documents / Visiting Card",
  helpText = "Photos, documents, PDFs, etc.",
  showLabel = true,
  disabled = false,
}: FileUploadSectionProps) {
  const imageFiles = files.filter((f) => f.type.startsWith("image/"));
  const nonImageFiles = files.filter((f) => !f.type.startsWith("image/"));

  // Stable blob URLs — created once per unique File object, revoked on cleanup
  const [imageUrls, setImageUrls] = useState<Map<File, string>>(new Map());
  useEffect(() => {
    const map = new Map<File, string>();
    imageFiles.forEach((file) => map.set(file, URL.createObjectURL(file)));
    setImageUrls(map);
    return () => {
      map.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]); // re-run only when the files array reference changes

  return (
    <div className="space-y-2">
      {showLabel && <Label className="text-sm font-medium">{label}</Label>}

      {/* Document Preview */}
      {files.length > 0 && (
        <div className="space-y-2 mb-2">
          {/* Image Previews Grid */}
          {imageFiles.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {imageFiles.map((file, index) => {
                const imageUrl = imageUrls.get(file);
                return (
                  <div
                    key={index}
                    className="relative aspect-square rounded-lg overflow-hidden border cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                        onClick={() => onImageClick?.(index)}
                      />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Find the actual index in the original files array
                        const actualIndex = files.indexOf(file);
                        onRemoveFile(actualIndex);
                      }}
                      className="absolute top-1 right-1 bg-destructive/90 text-destructive-foreground rounded-full p-1 hover:bg-destructive"
                      type="button"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Non-Image Files */}
          {nonImageFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {nonImageFiles.map((file, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1.5 py-1.5 px-2.5"
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span className="max-w-[100px] truncate text-xs">
                    {file.name}
                  </span>
                  <button
                    onClick={() => {
                      // Find the actual index in the original files array
                      const actualIndex = files.indexOf(file);
                      onRemoveFile(actualIndex);
                    }}
                    type="button"
                    className="ml-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload Options */}
      <div className="border-2 border-dashed border-border rounded-xl p-4">
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full justify-start rounded-xl"
            onClick={onCameraCapture}
            disabled={disabled}
          >
            <Camera className="h-4 w-4 mr-2" />
            Take Photo
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full justify-start rounded-xl"
            onClick={onFileUpload}
            disabled={disabled}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3 text-center">
          {helpText}
        </p>
      </div>

      {/* Image Viewer */}
      {imageViewerState && imageViewerState.isOpen && (
        <ImageViewer
          images={imageViewerState.images}
          currentIndex={imageViewerState.currentIndex}
          isOpen={imageViewerState.isOpen}
          onIndexChange={imageViewerState.setCurrentIndex}
          onClose={imageViewerState.onClose}
        />
      )}
    </div>
  );
}
